import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { currentPeriod, shiftPeriod, periodLabel, fmtMoney } from "../lib/salary";
import SalaryMonthTable from "../components/salary/SalaryMonthTable.jsx";
import EmployeeList from "../components/salary/EmployeeList.jsx";

export default function Salary() {
  const [tab, setTab] = useState("month");
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/salary/month", { params: { period } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { if (tab === "month") load(); }, [load, tab]);

  return (
    <div data-testid="salary-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Salary & Compensation</h1>
          <p className="text-slate-500 mt-1 text-sm inline-flex items-center gap-1.5">
            <Lock size={13} /> Private — visible to Dashan & Bhumi only
          </p>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {[["month", "Monthly Salary"], ["employees", "Employees"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`salary-tab-${k}`}
              className={`px-4 h-10 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {tab === "employees" ? (
        <EmployeeList />
      ) : (
        <>
          <div className="flex items-center gap-2 mb-6">
            <button type="button" onClick={() => setPeriod((p) => shiftPeriod(p, -1))} data-testid="salary-prev-month"
              className="w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50">
              <ChevronLeft size={18} />
            </button>
            <input type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} data-testid="salary-period-input"
              className="h-11 px-3 border border-slate-300 rounded-xl text-base font-semibold outline-none" />
            <button type="button" onClick={() => setPeriod((p) => shiftPeriod(p, 1))} data-testid="salary-next-month"
              className="w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50">
              <ChevronRight size={18} />
            </button>
            <div className="ml-2 font-display text-xl font-semibold text-slate-900" data-testid="salary-period-label">{periodLabel(period)}</div>
          </div>

          {data && data.summary.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="salary-summary">
              {data.summary.map((s) => (
                <div key={s.currency} className="border border-slate-200 rounded-xl bg-white p-4" data-testid={`salary-summary-${s.currency}`}>
                  <div className="section-label mb-2">{s.currency}</div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><div className="text-[11px] text-slate-500 uppercase">Total Due</div><div className="tabular font-semibold text-slate-900">{fmtMoney(s.total_due)}</div></div>
                    <div><div className="text-[11px] text-slate-500 uppercase">Paid</div><div className="tabular font-semibold text-emerald-700">{fmtMoney(s.paid)}</div></div>
                    <div><div className="text-[11px] text-slate-500 uppercase">Outstanding</div><div className={`tabular font-semibold ${s.outstanding > 0 ? "text-amber-700" : "text-slate-900"}`}>{fmtMoney(s.outstanding)}</div></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && !data ? <div className="text-slate-400">Loading…</div> : data && (
            <SalaryMonthTable rows={data.rows} period={period} onChanged={load} />
          )}
        </>
      )}
    </div>
  );
}
